import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PublicLayout from '../../components/PublicLayout';
import { 
  FaFileAlt, 
  FaCalendar,
  FaArrowRight,
  FaNewspaper,
  FaChartLine
} from 'react-icons/fa';
import { resourcesService } from '../../services/resources';
import '../../styles/ResourcesPage.css';

export default function ResourcesPage() {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchResources = async () => {
      try {
        setLoading(true);
        const response = await resourcesService.getPublicResources();
        
        // Handle different response formats
        if (response.data && response.data.data && Array.isArray(response.data.data)) {
          setResources(response.data.data);
        } else if (response.data && Array.isArray(response.data)) {
          setResources(response.data);
        } else if (response && Array.isArray(response)) {
          // Handle direct array response (fallback case)
          setResources(response);
        } else {
          throw new Error('Invalid response format');
        }
      } catch (err) {
        console.error('Error fetching resources:', err);
        // DEVELOPMENT SERVER FALLBACK: Don't show error, use fallback data
        const isDevelopmentServer = window.location.hostname.includes('vercel.app') || 
                                   window.location.hostname.includes('velox-ai.com');
        
        if (isDevelopmentServer) {
          // Use fallback data for development server
          setResources([
            {
              id: "fallback-1",
              s3Key: "public_resources/blog-articles/5 Ways AI is Revolutionizing Sales Conversations.md",
              title: "5 Ways AI is Revolutionizing Sales Conversations",
              description: "Comprehensive guide to implementing AI in your sales process",
              type: "blog article",
              author: "Velox AI Team",
              company: "",
              date: "2025-01-15T00:00:00.000Z",
              category: "Insights",
              industry: "",
              readTime: "5 min read",
              results: ""
            },
            {
              id: "fallback-2",
              s3Key: "public_resources/whitepapers/Building High-Performing Sales Teams- A Strategic Imperative for Sustainable Growth.md",
              title: "Building High-Performing Sales Teams: A Strategic Imperative for Sustainable Growth",
              description: "A comprehensive whitepaper on leveraging technology, especially AI, to build and empower high-performing sales teams for predictable revenue growth.",
              type: "whitepaper",
              author: "Velox AI Team",
              company: "",
              date: "2025-07-25T00:00:00.000Z",
              category: "Strategy",
              industry: "",
              readTime: "15 min read",
              results: ""
            }
          ]);
        } else {
          setError('Failed to load resources. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchResources();
  }, []);

  const getTypeColor = (type) => {
    switch (type) {
      case 'whitepaper':
        return '#3B82F6';
      case 'blog article':
        return '#10B981';
      case 'case study':
        return '#F59E0B';
      case 'use case':
        return '#F59E0B';
      default:
        return '#6B7280';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'whitepaper':
        return FaFileAlt;
      case 'blog article':
        return FaNewspaper;
      case 'case study':
        return FaChartLine;
      case 'use case':
        return FaChartLine;
      default:
        return FaFileAlt;
    }
  };

  const renderResourceCard = (resource, index) => {
    // Add safety check
    if (!resource.s3Key) {
      console.error('Resource missing s3Key:', resource);
      return null;
    }
    
    const IconComponent = getTypeIcon(resource.type);
    const typeColor = getTypeColor(resource.type);
    
    return (
      <div className="resources-page-card" key={resource.id || index}>
        <div className="resources-page-card-header">
          <div className="resources-page-card-icon" style={{ backgroundColor: typeColor }}>
            <IconComponent />
          </div>
          <span className="resources-page-type-tag" style={{ backgroundColor: typeColor }}>
            {resource.type}
          </span>
        </div>
        <div className="resources-page-card-content">
          <div className="resources-page-meta">
            {resource.category && <span className="resources-page-category">{resource.category}</span>}
            {resource.industry && <span className="resources-page-category">{resource.industry}</span>}
            <span className="resources-page-date">
              <FaCalendar />
              {new Date(resource.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
            </span>
            {resource.readTime && <span className="resources-page-read-time">{resource.readTime}</span>}
            {resource.results && <span className="resources-page-results">{resource.results}</span>}
          </div>
          <h3>{resource.title}</h3>
          <p>{resource.description}</p>
          
          <Link
            to={`/resources/${encodeURIComponent(resource.s3Key)}`}
            className="resources-page-btn resources-page-btn-primary"
            style={{ 
              position: 'relative', 
              zIndex: 10,
              pointerEvents: 'auto'
            }}
          >
            Read More <FaArrowRight />
          </Link>
        </div>
      </div>
    );
  };

  // OPTIMIZATION: Skeleton loading component
  const SkeletonCard = () => (
    <div className="resources-page-card skeleton-card">
      <div className="resources-page-card-header">
        <div className="resources-page-card-icon skeleton-icon"></div>
        <span className="resources-page-type-tag skeleton-tag"></span>
      </div>
      <div className="resources-page-card-content">
        <div className="resources-page-meta">
          <span className="resources-page-category skeleton-meta"></span>
          <span className="resources-page-date skeleton-meta"></span>
          <span className="resources-page-read-time skeleton-meta"></span>
        </div>
        <div className="skeleton-title"></div>
        <div className="skeleton-description"></div>
        <div className="skeleton-description short"></div>
        <div className="skeleton-button"></div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <PublicLayout>
        {/* Hero Section */}
        <section className="resources-page-hero">
          <div className="container">
            <h1>Resources & Insights</h1>
            <p>Discover the latest trends, strategies, and best practices in B2B sales and AI-powered revenue operations intelligence</p>
          </div>
        </section>
        
        {/* Skeleton Loading Section */}
        <section className="resources-page-section" id="resources">
          <div className="container">
            <div className="resources-page-grid">
              {[...Array(6)].map((_, index) => (
                <SkeletonCard key={index} />
              ))}
            </div>
          </div>
        </section>
        
        {/* CTA Section */}
        <section className="resources-page-cta">
          <div className="container">
            <h2>Ready to Bring Ambient Intelligence to Your Sales?</h2>
            <p>Equip your revenue team with always-on insights, seamless workflows, and deal intelligence that moves at the speed of your buyers</p>
            <Link to="/waitlist" className="btn btn-primary btn-large">Create Account</Link>
            <Link to="/contact" className="btn btn-secondary">Contact Our Team</Link>
          </div>
        </section>
      </PublicLayout>
    );
  }

  if (error) {
    return (
      <PublicLayout>
        <section className="resources-page-hero">
          <div className="container">
            <h1>Resources & Insights</h1>
            <p>{error}</p>
          </div>
        </section>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="resources-page-hero">
        <div className="container">
          <h1>Resources & Insights</h1>
          <p>Discover the latest trends, strategies, and best practices in B2B sales and AI-powered sales enablement</p>
        </div>
      </section>
      
      {/* Combined Resources Section */}
      <section className="resources-page-section" id="resources">
        <div className="container">
          
          {resources.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <p>No resources available at the moment. Please check back later.</p>
            </div>
          ) : (
            <div className="resources-page-grid">
              {resources.map((resource, index) => renderResourceCard(resource, index))}
            </div>
          )}
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="resources-page-cta">
        <div className="container">
          <h2>Ready to Bring Ambient Intelligence to Your Sales?</h2>
          <p>Equip your revenue team with always-on insights, seamless workflows, and deal intelligence that moves at the speed of your buyers</p>
          <Link to="/waitlist" className="btn btn-primary btn-large">Create Account</Link>
          <Link to="/contact" className="btn btn-secondary">Contact Our Team</Link>
        </div>
      </section>
    </PublicLayout>
  );
}